import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { Toaster } from 'sonner';
import "@/App.css";

// Context
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { TierLabelsProvider } from "@/context/TierLabelsContext";
import { AdminProvider } from "@/context/AdminContext";
import { FeaturesProvider } from "@/context/FeaturesContext";

// Page Components
import { AuthCallback, LoginPage, OnboardingPage } from "@/components/auth";
import ResetPasswordPage from "@/components/auth/ResetPasswordPage";
import ResubscribePage from "@/components/auth/ResubscribePage";
import { Dashboard } from "@/components/journal";
import LandingPage from "@/components/LandingPage";
import AdminDashboard from "@/components/AdminDashboard";
import NotificationsPage from "@/components/NotificationsPage";
import PrivacyPolicy from "@/components/legal/PrivacyPolicy";
import TermsOfService from "@/components/legal/TermsOfService";
import InviteRegistration from "@/components/InviteRegistration";
import B2BUserLogin from "@/components/B2BUserLogin";
import B2BForgotPassword from "@/components/B2BForgotPassword";
import B2BResetPassword from "@/components/B2BResetPassword";
import ClientPortal from "@/components/ClientPortal";
import NewClientPage from "@/components/NewClientPage";
import EditClientPage from "@/components/EditClientPage";
import UpgradePage from "@/pages/UpgradePage";
import UpgradeSuccess from "@/pages/UpgradeSuccess";
import UpgradeCancel from "@/pages/UpgradeCancel";
import WelcomePage from "@/components/WelcomePage";
import FoundingMemberCheckout from "@/components/FoundingMemberCheckout";
import ConfirmAccount from "@/components/ConfirmAccount";
import RedeemCodePage from "@/components/RedeemCodePage";

// Dialog Context
import { DialogProvider } from "@/context/DialogContext";

// Analytics
import { trackPageView, startSession } from "@/utils/analytics";
import { trackPageView as fbTrackPageView, initPixel } from "@/utils/fbPixel";

// ==================== ANALYTICS TRACKER ====================
const AnalyticsTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Initialize Facebook Pixel with dynamic config from backend
    initPixel();
    
    // Defer analytics - these are non-critical
    const timer = setTimeout(() => {
      startSession();
    }, 3000);
    return () => clearTimeout(timer);
  }, []);
  
  useEffect(() => {
    // Defer page view tracking - non-critical
    const timer = setTimeout(() => {
      trackPageView(location.pathname);
      // Also track with Facebook Pixel
      fbTrackPageView();
    }, 2500);
    return () => clearTimeout(timer);
  }, [location.pathname]);
  
  return null;
};

// ==================== PROTECTED ROUTE ====================
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check for preview mode in URL
  const searchParams = new URLSearchParams(location.search);
  const isPreviewMode = searchParams.has('preview');
  
  // Check for session_id in hash - this indicates OAuth callback in progress
  const hasSessionId = window.location.hash?.includes('session_id=');
  
  // Determine if we're on a B2B portal URL and extract clientId
  const isOnB2BPortalUrl = location.pathname.includes('/portal/');
  const clientIdMatch = location.pathname.match(/\/portal\/([^\/]+)/);
  const urlClientId = clientIdMatch ? clientIdMatch[1] : null;

  useEffect(() => {
    // Allow preview mode to bypass authentication
    if (isPreviewMode) return;
    
    // Skip redirect logic if OAuth callback is in progress
    if (hasSessionId) return;
    
    // If user data passed from AuthCallback, we're authenticated
    if (location.state?.user) return;
    
    if (!loading && !user) {
      // Redirect to the appropriate login page based on URL context
      if (isOnB2BPortalUrl && urlClientId) {
        navigate(`/portal/${urlClientId}/login`);
      } else {
        navigate("/login");
      }
    }
    
    // Redirect to onboarding if onboarding_complete is false/missing (unless already there)
    // Skip onboarding for B2B users if their client has disabled it
    if (!loading && user && !user.onboarding_complete && location.pathname !== '/onboarding') {
      // Check if user is B2B and their client has disabled onboarding
      const b2bOnboardingDisabled = user.b2b_client_id && user.client_features?.enable_onboarding === false;
      
      if (!b2bOnboardingDisabled) {
        navigate("/onboarding");
      }
    }
  }, [user, loading, navigate, location.state?.user, location.pathname, hasSessionId, isPreviewMode]);

  // Allow preview mode to render immediately
  if (isPreviewMode) {
    return children;
  }

  // Show loading if OAuth callback in progress
  if (hasSessionId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#2D063E] to-[#4A0A66]">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#2D063E] to-[#4A0A66]">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (!user && !location.state?.user) {
    return null;
  }

  return children;
};

// ==================== APP ROUTER ====================
const AppRouter = () => {
  // CRITICAL: Check for session_id using window.location.hash directly
  // React Router's useLocation() may not include the hash fragment properly
  const hasSessionId = window.location.hash?.includes("session_id=");

  // Process OAuth callback FIRST before any other routing
  if (hasSessionId) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/resubscribe" element={<ResubscribePage />} />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <OnboardingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        }
      />
      <Route path="/admin" element={
        <AdminProvider>
          <AdminDashboard />
        </AdminProvider>
      } />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/welcome" element={<WelcomePage />} />
      <Route path="/founding-member" element={<FoundingMemberCheckout />} />
      <Route path="/redeem" element={<RedeemCodePage />} />
      <Route path="/confirm-account" element={<ConfirmAccount />} />
      <Route path="/register/invite/:token" element={<InviteRegistration />} />
      <Route path="/portal/:clientId" element={<B2BUserLogin />} />
      <Route path="/portal/:clientId/login" element={<B2BUserLogin />} />
      <Route path="/portal/:clientId/forgot-password" element={<B2BForgotPassword />} />
      <Route path="/portal/:clientId/reset-password/:token" element={<B2BResetPassword />} />
      <Route 
        path="/portal/:clientId/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/portal/:clientId/notifications" 
        element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        } 
      />
      <Route path="/client-portal" element={<ClientPortal />} />
      <Route path="/admin/b2b/new-client" element={<NewClientPage />} />
      <Route path="/admin/b2b/edit-client/:clientId" element={<EditClientPage />} />
      <Route 
        path="/upgrade" 
        element={
          <ProtectedRoute>
            <UpgradePage />
          </ProtectedRoute>
        } 
      />
      <Route path="/upgrade/success" element={<UpgradeSuccess />} />
      <Route path="/upgrade/cancel" element={<UpgradeCancel />} />
    </Routes>
  );
};

// ==================== THEMED APP ====================
const ThemedApp = () => {
  const { user } = useAuth();
  const location = useLocation();
  
  // Use React Router's location to properly detect B2B portal URLs on navigation
  // This ensures theme changes when navigating between main app and B2B portals
  const isOnB2BPortalUrl = location.pathname.includes('/portal/');
  
  // For B2B licensed users on B2B portal URL, use client_theme; otherwise use personal theme
  // This converts client_theme format to the ThemeContext format
  // IMPORTANT: Pass null explicitly when NOT on B2B portal to trigger default theme reset
  // B2B users ALWAYS get flat mode - no gradients allowed
  const effectiveTheme = (isOnB2BPortalUrl && user?.client_theme) ? {
    primary: user.client_theme.primary_color,
    secondary: user.client_theme.secondary_color,
    background: user.client_theme.background_primary,
    background_secondary: user.client_theme.background_secondary,
    text: user.client_theme.text_primary,
    text_secondary: user.client_theme.text_secondary,
    border: user.client_theme.border_color,
    logo_primary: user.client_theme.primary_color,
    logo_secondary: user.client_theme.secondary_color,
    mode: 'flat',  // FORCED to flat for B2B - no gradients
    _isB2B: true   // Flag to identify this as B2B theme
  } : user?.theme || null;
  
  return (
    <ThemeProvider 
      userTheme={effectiveTheme} 
      userSkinTone={user?.emoji_skin_tone}
      isB2BPortal={isOnB2BPortalUrl}
    >
      <FeaturesProvider>
        <AppRouter />
      </FeaturesProvider>
    </ThemeProvider>
  );
};

// ==================== MAIN APP ====================
function App() {
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  
  return (
    <div className="App">
      {/* Global Toast Notifications - Uses CSS variables for B2B theming */}
      <Toaster 
        position="top-center"
        toastOptions={{
          style: {
            background: 'var(--theme-primary, #E91E63)',
            color: 'var(--theme-button-text, white)',
            border: '1px solid var(--theme-primary, #E91E63)',
          },
          classNames: {
            success: 'b2b-toast-success',
            error: 'b2b-toast-error',
            info: 'b2b-toast-info',
          }
        }}
      />
      
      <GoogleReCaptchaProvider
        reCaptchaKey={recaptchaSiteKey || ""}
        scriptProps={{
          async: true,
          defer: true,
          appendTo: "head",
        }}
      >
        <BrowserRouter>
          <DialogProvider>
            <AnalyticsTracker />
            <AuthProvider>
              <TierLabelsProvider>
                <ThemedApp />
              </TierLabelsProvider>
            </AuthProvider>
          </DialogProvider>
        </BrowserRouter>
      </GoogleReCaptchaProvider>
    </div>
  );
}

export default App;
